<template>
    <HeaderCardsLayout v-if="series" variant="No-Logo">
        <template #headline-extra>
            <BadgeDropDown
                :label="$t('status')"
                class="text-base font-normal"
                :current-value="getStatusBadgeCurrentValue()"
                :values="getStatusBadgeItems()"
                :status="getStatusBadgeStatus()"
                :tooltip="statusBadgeDisabledExplanation"
                size="Large-40"
                @change-value="(value: string) => handleStatusBadgeChange(value)"
            />
        </template>
        <template #headline>{{ series.nameShort }}</template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="series.nameLong">
                <BreadcrumbLink :target="{ name: 'external-events' }">
                    <AirplaneIcon class="mr-1 shrink-0" />
                    {{ $t('external-events') }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #buttons>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteSeries"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
            <LinkButton
                variant="Info-Strong"
                size="Base"
                :disabled-explanation="createEventDisabledExplanation"
                :to="getCreateExternalEventLink(seriesId, series.nameShort, series.nameLong)"
            >
                <template #icon>
                    <PlusIcon class="size-5" />
                </template>
                {{ $t('external-event-add') }}
            </LinkButton>
        </template>
        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                :tab-panel-id="tabPanelId"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="tabPanelRef.focus()"
            />
        </template>
        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView />
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import BadgeDropDown from '@/components/BadgeDropDown/BadgeDropDown.vue';
import { useBadgeDropDown } from '@/components/BadgeDropDown/useBadgeDropDown';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import AirplaneIcon from '@/components/Icon/AirplaneIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useTabNavigation } from '@/components/TabNavigation/useTabNavigation';
import { ExternalEventSeriesStatus, UpdateExternalEventSeriesStatusDocument } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { useExternalEventSeries } from '../composables/useExternalEventSeries';
import {
    externalEventSeriesStatusToBadgeVariant,
    getCreateExternalEventLink,
    getExternalEventSeriesStatusLabel,
} from '../externalEvent';
import { externalEventSeriesKey } from '../keys';

const props = defineProps<{ seriesId: string }>();

const tabPanelRef = ref();
const tabPanelId = nanoid();
const fluent = useFluent();

const seriesId = computed(() => props.seriesId);

const {
    series,
    seriesStatus,
    deleteDisabledExplanation,
    createEventDisabledExplanation,
    deleteSeries,
    externalEventSeriesUpdate,
    ...rest
} = useExternalEventSeries(seriesId);

provide(externalEventSeriesKey, {
    series,
    seriesStatus,
    deleteDisabledExplanation,
    createEventDisabledExplanation,
    deleteSeries,
    externalEventSeriesUpdate,
    ...rest,
});

const statusOrder = ['active', 'completed', 'discontinued'];

const {
    statusBadgeDisabledExplanation,
    getStatusBadgeCurrentValue,
    getStatusBadgeItems,
    getStatusBadgeStatus,
    handleStatusBadgeChange,
} = useBadgeDropDown(
    seriesStatus,
    seriesId,
    UpdateExternalEventSeriesStatusDocument,
    getExternalEventSeriesStatusLabel,
    externalEventSeriesStatusToBadgeVariant,
    Object.values(ExternalEventSeriesStatus).sort((x, y) => statusOrder.indexOf(x) - statusOrder.indexOf(y)),
);

const tabs = [
    { route: 'external-event-series-events-tab', label: fluent.$t('external-event-events-tab') },
    { route: 'external-event-series-masterdata-tab', label: fluent.$t('master-data') },
];

const { tabNavigationItems, tabsDisabledText, activeTabHeadline } = useTabNavigation(tabs);

useHeadSafe({ title: () => `${series.value?.nameShort ?? ''} - ${$t('external-events')}` });
</script>
